import React, { useState, useEffect } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { citiesService } from '../../../services/dataService';

const BasicInfoSection = ({ formData, onUpdate, errors = {} }) => {
  const [imagePreview, setImagePreview] = useState(formData?.logo || null);
  const [isUploading, setIsUploading] = useState(false);
  const [cities, setCities] = useState([]);
  const [isLoadingCities, setIsLoadingCities] = useState(true);

  // Fetch cities from database
  useEffect(() => {
    const fetchCities = async () => {
      try {
        setIsLoadingCities(true);
        const citiesData = await citiesService.getAll();
        
        // Transform cities to Select component format
        const citiesOptions = citiesData?.map(city => ({
          value: city?.id?.toString(),
          label: `${city?.nameAr}${city?.nameEn ? ` - ${city?.nameEn}` : ''}${city?.nameTr ? ` - ${city?.nameTr}` : ''}`
        })) || [];
        
        setCities(citiesOptions);
      } catch (err) {
        console.error('Error fetching cities:', err);
        setCities([]);
      } finally {
        setIsLoadingCities(false);
      }
    };

    fetchCities();
  }, []);

  const statusOptions = [
    { value: 'active', label: 'نشط - Active' },
    { value: 'inactive', label: 'غير نشط - Inactive' },
    { value: 'pending', label: 'في الانتظار - Pending' },
    { value: 'suspended', label: 'معلق - Suspended' }
  ];

  const handleInputChange = (field, value) => {
    onUpdate({ [field]: value });
  };

  const handleImageUpload = (event) => {
    const file = event?.target?.files?.[0];
    if (file) {
      if (file?.size > 5 * 1024 * 1024) { // 5MB limit
        return;
      }

      setIsUploading(true);
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageUrl = e?.target?.result;
        setImagePreview(imageUrl);
        handleInputChange('logo', imageUrl);
        setIsUploading(false);
      };
      reader?.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setImagePreview(null);
    handleInputChange('logo', '');
  };

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div className="flex items-center space-x-3 pb-4 border-b border-border">
        <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
          <Icon name="Info" size={18} color="white" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground font-arabic">
            المعلومات الأساسية
          </h3>
          <p className="text-sm text-muted-foreground font-caption">
            أدخل المعلومات الأساسية للاتحاد الفرعي
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Form Fields */}
        <div className="space-y-4">
          {/* Arabic Name */}
          <Input
            label="الاسم بالعربية *"
            type="text"
            placeholder="أدخل اسم الاتحاد الفرعي بالعربية"
            value={formData?.nameAr || ''}
            onChange={(e) => handleInputChange('nameAr', e?.target?.value)}
            error={errors?.nameAr}
            required
            className="text-right"
            dir="rtl"
          />

          {/* English Name */}
          <Input
            label="الاسم بالإنجليزية *"
            type="text"
            placeholder="Enter sub-union name in English"
            value={formData?.nameEn || ''}
            onChange={(e) => handleInputChange('nameEn', e?.target?.value)}
            error={errors?.nameEn}
            required
          />

          {/* Turkish Name */}
          <Input
            label="الاسم بالتركية"
            type="text"
            placeholder="Türkçe alt birlik adını girin"
            value={formData?.nameTr || ''}
            onChange={(e) => handleInputChange('nameTr', e?.target?.value)}
            error={errors?.nameTr}
          />

          {/* City Selection */}
          <Select
            label="المدينة *"
            placeholder={isLoadingCities ? 'جاري تحميل المدن...' : 'اختر المدينة'}
            options={cities}
            value={formData?.cityId || ''}
            onChange={(value) => handleInputChange('cityId', value)}
            error={errors?.cityId}
            required
            searchable
            disabled={isLoadingCities}
            className="mb-4"
          />

          {/* Status - Commented out per user request */}
          {/* <Select
            label="الحالة *"
            placeholder="اختر حالة الاتحاد"
            options={statusOptions}
            value={formData?.status || 'active'}
            onChange={(value) => handleInputChange('status', value)}
            error={errors?.status}
            required
            className="mb-4"
          /> */}
        </div>

        {/* Right Column - Logo Upload */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2 font-arabic">
              شعار الاتحاد الفرعي
            </label>
            <p className="text-xs text-muted-foreground mb-4 font-caption">
              ارفع شعار الاتحاد الفرعي (الحد الأقصى: 5 ميجابايت)
            </p>

            {/* Image Preview */}
            <div className="border-2 border-dashed border-border rounded-lg p-6 text-center bg-muted/30">
              {imagePreview ? (
                <div className="space-y-4">
                  <div className="relative inline-block">
                    <Image
                      src={imagePreview}
                      alt="Sub-union logo preview showing uploaded organization emblem"
                      className="w-32 h-32 object-cover rounded-lg shadow-sm"
                    />
                    <Button
                      variant="destructive"
                      size="icon"
                      className="absolute -top-2 -right-2 w-6 h-6"
                      onClick={removeImage}
                      iconName="X"
                      iconSize={14}
                    />
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-foreground">
                      تم رفع الشعار بنجاح
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => document.getElementById('logo-upload')?.click()}
                      iconName="Upload"
                      iconPosition="left"
                      iconSize={16}
                    >
                      تغيير الشعار
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="w-16 h-16 bg-muted rounded-lg flex items-center justify-center mx-auto">
                    <Icon name="Image" size={24} className="text-muted-foreground" />
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-foreground">
                      ارفع شعار الاتحاد الفرعي
                    </p>
                    <p className="text-xs text-muted-foreground">
                      PNG, JPG, GIF حتى 5 ميجابايت
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => document.getElementById('logo-upload')?.click()}
                    loading={isUploading}
                    iconName="Upload"
                    iconPosition="left"
                    iconSize={16}
                  >
                    {isUploading ? 'جاري الرفع...' : 'اختر ملف'}
                  </Button>
                </div>
              )}

              <input
                id="logo-upload"
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
            </div>

            {errors?.logo && (
              <p className="text-sm text-error mt-2 font-caption">
                {errors?.logo}
              </p>
            )}
          </div>

          {/* Upload Guidelines */}
          <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Icon name="Info" size={16} className="text-accent mt-0.5" />
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-accent font-arabic">
                  إرشادات رفع الشعار
                </h4>
                <ul className="text-xs text-accent/80 space-y-1 font-caption">
                  <li>• استخدم صورة مربعة للحصول على أفضل النتائج</li>
                  <li>• الحد الأدنى للدقة: 200x200 بكسل</li>
                  <li>• الصيغ المدعومة: PNG, JPG, GIF</li>
                  <li>• الحد الأقصى لحجم الملف: 5 ميجابايت</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Additional Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Establishment Date */}
        <Input
          label="تاريخ التأسيس"
          type="date"
          value={formData?.establishedDate || ''}
          onChange={(e) => handleInputChange('establishedDate', e?.target?.value)}
          error={errors?.establishedDate}
        />

        {/* Registration Number - Commented out per user request */}
        {/* <Input
          label="رقم التسجيل"
          type="text"
          placeholder="أدخل رقم التسجيل الرسمي"
          value={formData?.registrationNumber || ''}
          onChange={(e) => handleInputChange('registrationNumber', e?.target?.value)}
          error={errors?.registrationNumber}
        /> */}
      </div>

      {/* Contact Information - Commented out per user request */}
      <div className="space-y-4 pt-4 border-t border-border">
        <h4 className="text-md font-semibold text-foreground font-arabic">
          معلومات الاتصال الأساسية
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="البريد الإلكتروني أو رقم الهاتف"
            type="text"
            placeholder="example@email.com or +963 XX XXX XXXX"
            value={formData?.phone || ''}
            onChange={(e) => handleInputChange('phone', e?.target?.value)}
            error={errors?.phone}
          />

          {/* <Input
            label="البريد الإلكتروني للاتصال"
            type="email"
            placeholder="example@email.com"
            value={formData?.contactEmail || ''}
            onChange={(e) => handleInputChange('contactEmail', e?.target?.value)}
            error={errors?.contactEmail}
          /> */}
        </div>

        <div className="space-y-4">
          <h5 className="text-sm font-medium text-foreground font-arabic">
            أرقام الاتصال متعددة اللغات
          </h5>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="رقم الاتصال بالعربية"
              type="tel"
              placeholder="+963 XX XXX XXXX"
              value={formData?.contactNumberAr || ''}
              onChange={(e) => handleInputChange('contactNumberAr', e?.target?.value)}
              error={errors?.contactNumberAr}
              className="text-right"
              dir="rtl"
            />

            <Input
              label="Contact Number (English)"
              type="tel"
              placeholder="+963 XX XXX XXXX"
              value={formData?.contactNumberEn || ''}
              onChange={(e) => handleInputChange('contactNumberEn', e?.target?.value)}
              error={errors?.contactNumberEn}
            />

            <Input
              label="İletişim Numarası (Türkçe)"
              type="tel"
              placeholder="+963 XX XXX XXXX"
              value={formData?.contactNumberTr || ''}
              onChange={(e) => handleInputChange('contactNumberTr', e?.target?.value)}
              error={errors?.contactNumberTr}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BasicInfoSection;